import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import type { FlexProps } from '@withjoy/joykit';
import { Flex, keyframes, styled } from '@withjoy/joykit';
import React, { useEffect, useState } from 'react';
import { CurrentSelectionStrategy } from './SelectionStrategy';
import { startDrag } from './startDrag';
import { useSurface } from './useSurface';
import { useClickOutside } from '@shared/hooks/useClickOutside';

const INTRO_HIGHLIGHT_COLOR = '#4E06F2';
const pulseKeyframes = keyframes`
	30%  {outline-color: ${INTRO_HIGHLIGHT_COLOR}FF;}
	50%  {outline-color: ${INTRO_HIGHLIGHT_COLOR}40;}
	70%  {outline-color: ${INTRO_HIGHLIGHT_COLOR}FF;}
`;

const INTRO_DURATION = 3000;
const StyledSurface = styled(Flex)<{ scale: number }>`
  &.intro .introable {
    outline: ${({ scale }) => 4 / scale}px solid transparent;
    border-radius: 8px;
    animation-name: ${pulseKeyframes};
    animation-duration: ${INTRO_DURATION}ms;
    animation-timing-function: ease-in-out;
  }
`;

export const Surface: React.FC<FlexProps> = ({ children, ...props }) => {
  const isMobileOrTablet = useIsMobileOrTablet();
  const [surface, update] = useSurface();

  const ref = useClickOutside<HTMLDivElement>(() => {
    update(surface => {
      surface.hoverLayerIndex = undefined;
      surface.activeLayerIndex = undefined;
      surface.editingLayerIndex = undefined;
    });
  });

  // Explicit on/off switch at the Surface level prevents
  // the CSS animation from replaying when React remove/adds DOM elements
  // such as entering and exiting text editing mode.
  const [showIntro, setShowIntro] = useState(true);
  useEffect(() => {
    const id = setTimeout(() => setShowIntro(false), INTRO_DURATION);
    return () => clearTimeout(id);
  }, []);

  return (
    <StyledSurface
      ref={ref}
      className={showIntro ? 'intro' : ''}
      scale={surface.scale}
      width="100%"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      paddingX={5}
      paddingTop={isMobileOrTablet ? 6 : 4}
      paddingBottom={isMobileOrTablet ? 6 : 10}
      position="relative"
      userSelect="none" // Prevent long press text selection
      onPointerDown={startDrag(
        () => {},
        undefined,
        didDrag => {
          if (didDrag) return;
          CurrentSelectionStrategy.surfacePointerDown?.(update);
        },
      )}
      onBlur={() => {
        if (!isMobileOrTablet) return;
        // Blur is our signal the mobile keyboard is dismissed.
        update(surface => {
          surface.editingLayerIndex = undefined;
        });
      }}
      {...props}
    >
      {children}
    </StyledSurface>
  );
};
